import { createAsyncThunk } from '@reduxjs/toolkit';
import { loadVendorData } from '@/services/firebase/vendorFirestoreService';
import { hydrateFromFirestore as hydrateForm } from '@/store/slices/formSlice';
import { hydrateFromFirestore as hydrateEntity } from '@/store/slices/entitySlice';
import { hydrateFromFirestore as hydrateVerification } from '@/store/slices/verificationSlice';
import { hydrateFromFirestore as hydrateConsent } from '@/store/slices/consentSlice';
import { setReferenceId } from '@/store/slices/vendorSlice';
import { getStepConfig } from '@/config/stepConfigs';
import type { EntityType } from '@/types/onboarding';

/**
 * Loads the vendor document from Firestore and hydrates all Redux slices.
 * Should be dispatched right after a successful login or session restore.
 *
 * Returns the path of the step the user should resume at, or null if the user
 * is new (no saved step) or the document does not exist yet.
 */
export const loadVendorFromFirestore = createAsyncThunk(
  'vendor/loadFromFirestore',
  async (uid: string, { dispatch }) => {
    const data = await loadVendorData(uid);
    if (!data) return null;

    const entityType = data.entityType as EntityType | undefined;

    // Restore entity type and business details
    dispatch(
      hydrateEntity({
        entityType,
        businessDetails: data.businessDetails as any,
        partners: data.partners as any,
      }),
    );

    // Restore form fields (basic details, address, bank, PAN/GST)
    dispatch(
      hydrateForm({
        basicDetails: data.basicDetails as any,
        address: data.address as any,
        bankDetails: data.bankDetails as any,
        panNumber: data.panNumber as string | undefined,
        panName: data.panName as string | undefined,
        gstNumber: data.gstNumber as string | undefined,
        gstBusinessName: data.gstBusinessName as string | undefined,
      }),
    );

    // Restore verification status for each type
    if (data.verificationStatus && typeof data.verificationStatus === 'object') {
      dispatch(hydrateVerification(data.verificationStatus as any));
    }

    // Restore granted consent categories
    if (Array.isArray(data.grantedConsents)) {
      dispatch(hydrateConsent(data.grantedConsents as string[]));
    }

    // Restore reference ID if previously persisted
    if (typeof data.referenceId === 'string' && data.referenceId) {
      dispatch(setReferenceId(data.referenceId));
    }

    // Resolve and return the resume step path
    const currentStepId = data.currentStepId as string | undefined;
    if (currentStepId) {
      const steps = getStepConfig(entityType ?? null);
      const step = steps.find((s) => s.id === currentStepId);
      return step?.path ?? null;
    }

    return null;
  },
);
